import React, { useId } from "react";
import type { TextNodeType } from "../../../FormattedText/FormattedText";
import FormattedText from "../../../FormattedText/FormattedText";
import {
  CardBackground,
  CardForeground,
  ExtraFooter,
  GraphicCol,
  LineHeading,
  LineSubtitle,
  LineTitle,
  PersuasionCard,
  Section,
  TextCol,
} from "./Styles";

function DefaultPeelGraphic() {
  const gradId = useId().replace(/:/g, "");
  return (
    <svg
      width="75"
      height="91"
      viewBox="0 0 75 91"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{
        position: "relative",
        zIndex: 1,
        marginTop: -15,
        marginRight: -15,
      }}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#c86dd7" />
          <stop offset="50%" stopColor="#7c3aed" />
          <stop offset="100%" stopColor="#3023ae" />
        </linearGradient>
      </defs>
      <path
        d="M0 0h59c8.8 0 16 7.2 16 16v59c0 8.8-7.2 16-16 16H0V0z"
        fill={`url(#${gradId})`}
      />
      <path
        d="M10 10 L65 40 L40 85 Q25 72 20 56 V22 Q18 14 10 10Z"
        fill="#9333ea"
        opacity="0.2"
      />
      <circle cx="46" cy="24" r="14" fill="#facc15" />
      <text
        x="46"
        y="30"
        textAnchor="middle"
        fontSize="16"
        fontWeight="700"
        fill="#000"
        fontFamily="Lato, system-ui, sans-serif"
      >
        ₹
      </text>
    </svg>
  );
}

function footerLines(footerInfo?: string | string[], info?: string): string[] {
  if (Array.isArray(footerInfo) && footerInfo.length) return footerInfo;
  if (typeof footerInfo === "string" && footerInfo.trim()) {
    return footerInfo.split(/\n|<br\s*\/?>/i).map((s) => s.trim());
  }
  if (typeof info === "string" && info.trim()) {
    return info.split(/\n|<br\s*\/?>/i).map((s) => s.trim());
  }
  return [];
}

/**
 * `PWA_listing.json` — `COMPONENT_LISTING_PERSUASION`:
 * `heading`, `title`, `subtitle` (FormattedText-shaped), `bgImgUrl`.
 *
 * UI: one purple-bordered card; `bgImgUrl` covers the full card background; three text tiers;
 * default peel + ₹ on the right only when there is no background image.
 */
export interface ListingPersuasionProps {
  bgImgUrl?: string;
  heading?: string | TextNodeType;
  title?: string | TextNodeType;
  subtitle?: string | TextNodeType;
  /** Optional extra grey lines (not in base contract) */
  footerInfo?: string | string[];
  info?: string;
  /** @deprecated prefer `bgImgUrl` */
  illustrationUrl?: string;
}

export const ListingPersuasion = ({
  bgImgUrl,
  heading,
  title,
  subtitle,
  footerInfo,
  info,
  illustrationUrl,
}: ListingPersuasionProps) => {
  const imageSrc = (bgImgUrl || illustrationUrl)?.trim();
  const extraLines = footerLines(footerInfo, info);

  if (
    heading == null &&
    title == null &&
    subtitle == null &&
    !imageSrc &&
    extraLines.length === 0
  ) {
    return null;
  }

  return (
    <Section>
      <PersuasionCard $hasBg={Boolean(imageSrc)}>
        {imageSrc ? <CardBackground $src={imageSrc} aria-hidden /> : null}
        <CardForeground>
          <TextCol>
            {heading != null ? (
              <LineHeading>
                <FormattedText value={heading} />
              </LineHeading>
            ) : null}
            {title != null ? (
              <LineTitle>
                <FormattedText value={title} />
              </LineTitle>
            ) : null}
            {subtitle != null ? (
              <LineSubtitle>
                <FormattedText value={subtitle} />
              </LineSubtitle>
            ) : null}
            {extraLines.length > 0 ? (
              <ExtraFooter>
                {extraLines.map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    {line}
                  </React.Fragment>
                ))}
              </ExtraFooter>
            ) : null}
          </TextCol>
          {!imageSrc ? (
            <GraphicCol>
              <DefaultPeelGraphic />
            </GraphicCol>
          ) : null}
        </CardForeground>
      </PersuasionCard>
    </Section>
  );
};
